import { ArrowDown, ArrowUp, Check, Pencil, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { apiSend, useApi } from "../api";
import type { PortalConfig } from "../config";
import { confirmBox } from "../confirm";
import type { Lookups, Status } from "../types";

/** What the app does with each system status, shown as a read-only badge. */
export const SYSTEM_USES: Record<string, string> = {
  mail_labels: "new leads waiting for a label",
  label_sent: "lead label sent; 3-day label text",
  label_sent_old: "Leads page and the 90-day archive",
  verify_address: "leads whose address needs checking",
  insurance_selected: "leads that asked for insurance",
  lead_archived: "archived leads (Leads page, reports)",
  mail_box: "mailer box requested",
  mail_label: "mailer envelope requested",
  mailer_outgoing: "mailer on its way to the customer",
  mailer_arrived: "mailer delivered",
  in_transit: "label 1 scanned by the carrier",
  arrived_at_store: "label 1 delivered to the shop",
  pending_inspection: "check-in",
  estimate_sent: "Publish estimate; estimate follow-ups",
  limbo_estimate: "after the last estimate reminder",
  in_progress: "customer approved the estimate",
  customer_declined_estimate: "customer declined the estimate",
  invoiced: "Convert to Invoice; invoice reminders",
  limbo_invoice: "after the last invoice reminder",
  ready_to_ship: "paid in full after the invoice",
  awaiting_pickup: "after Print Shipping Labels",
  out_transit: "label 2 scanned by the carrier",
  resolved: "label 2 delivered; review requests; reports",
  returned: "Return ticket",
};

const byOrder = (a: Status, b: Status) => Number(a.sortOrder) - Number(b.sortOrder) || a.id - b.id;

/** Switch between the two list pages that share the Services nav item (keeps the top bar short). */
export function ListsSwitch({ on }: { on: "services" | "statuses" }) {
  return (
    <nav className="seg-toggle" aria-label="Lists">
      <Link to="/services" className={on === "services" ? "on" : ""} aria-current={on === "services" ? "page" : undefined}>Services</Link>
      <Link to="/statuses" className={on === "statuses" ? "on" : ""} aria-current={on === "statuses" ? "page" : undefined}>Statuses</Link>
    </nav>
  );
}

/**
 * Statuses (owner review item 21, 2026-11-04: "Add a section to manage Statuses that would avoid having these
 * issues"). Ticket status is a staff-managed list: add, rename (system statuses too: the app finds them by their
 * system key, not their name), reorder, finished / work-queue flags, move a status's tickets elsewhere, retire.
 * System statuses can't be retired or have "finished" changed. Any staff may use it for now (owner).
 */
export function StatusesPage({ config }: { config: PortalConfig }) {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const [reload, setReload] = useState(0);
  const { data, error } = useApi<Lookups>(config, "/lookups", reload);
  const [newName, setNewName] = useState("");
  const [renaming, setRenaming] = useState<{ id: number; name: string } | null>(null);
  const [moving, setMoving] = useState<{ id: number; to: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState("");
  const [saved, setSaved] = useState("");

  const all = useMemo(() => [...(data?.statuses ?? [])].sort(byOrder), [data]);
  const active = all.filter((s) => s.isActive), retired = all.filter((s) => !s.isActive);

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true); setProblem(""); setSaved("");
    try { await fn(); setSaved(`${done} Other open pages show it after a reload.`); setReload((n) => n + 1); return true; }
    catch (e) { setProblem((e as Error).message); return false; }
    finally { setBusy(false); }
  };
  const put = (s: Status, body: Record<string, unknown>, done: string) => run(() => apiSend(config, token, "PUT", `/statuses/${s.id}`, body), done);
  const move = (index: number, by: -1 | 1) => {
    const list = [...active];
    const [s] = list.splice(index, 1);
    list.splice(index + by, 0, s!);
    void run(() => apiSend(config, token, "POST", "/statuses/reorder", { ids: [...list, ...retired].map((x) => x.id) }), `Moved ${s!.name}.`);
  };
  const add = async () => {
    if (await run(() => apiSend(config, token, "POST", "/statuses", { name: newName }), `Added ${newName.trim()} at the end of the list.`)) setNewName("");
  };
  const retire = async (s: Status) => {
    if (s.tickets > 0) { setMoving({ id: s.id, to: "" }); return; }
    if (await confirmBox(`Retire "${s.name}"? It won't be offered any more; its history stays.`, { title: "Retire status", ok: "Retire" })) {
      await put(s, { isActive: false }, `Retired ${s.name}.`);
    }
  };
  const moveAndRetire = async (s: Status, toId: number) => {
    const to = all.find((x) => x.id === toId)!;
    const n = s.tickets;
    if (!(await confirmBox(`Move ${n} ticket${n === 1 ? "" : "s"} from "${s.name}" to "${to.name}", then retire "${s.name}"?\n\nNo email or text is sent to customers.`,
      { title: "Move tickets and retire", ok: "Move and retire" }))) return;
    if (await run(() => apiSend(config, token, "POST", `/statuses/${s.id}/move-tickets`, { toStatusId: toId, retire: true }),
      `Moved ${n} ticket${n === 1 ? "" : "s"} to ${to.name} and retired ${s.name}.`)) setMoving(null);
  };

  const row = (s: Status, i: number) => {
    const system = s.systemKey ? SYSTEM_USES[s.systemKey] ?? s.systemKey.replace(/_/g, " ") : null;
    return (
      <tr key={s.id} className={s.isActive ? "" : "retired"}>
        <td className="nowrap">{s.isActive && <>
          <button type="button" className="icon-link" aria-label={`Move ${s.name} up`} disabled={busy || i === 0} onClick={() => move(i, -1)}><ArrowUp size={15} /></button>
          <button type="button" className="icon-link" aria-label={`Move ${s.name} down`} disabled={busy || i === active.length - 1} onClick={() => move(i, 1)}><ArrowDown size={15} /></button>
        </>}</td>
        <td>
          {renaming?.id === s.id ? (
            <form className="inline-form" onSubmit={(e) => { e.preventDefault(); void put(s, { name: renaming.name }, `Renamed to ${renaming.name.trim()}.`).then((ok) => ok && setRenaming(null)); }}>
              <input value={renaming.name} autoFocus maxLength={60} aria-label="Status name" onChange={(e) => setRenaming({ id: s.id, name: e.target.value })} />
              <button type="submit" className="icon-link" aria-label="Save name" disabled={busy || !renaming.name.trim()}><Check size={15} /></button>
              <button type="button" className="icon-link" aria-label="Cancel" onClick={() => setRenaming(null)}><X size={15} /></button>
            </form>
          ) : (
            <><strong>{s.name}</strong>{!s.isActive && <span className="tag">retired</span>}
              <button type="button" className="icon-link" aria-label={`Rename ${s.name}`} onClick={() => setRenaming({ id: s.id, name: s.name })}><Pencil size={14} /></button></>
          )}
          {system && <div><span className="badge outline" title="The app finds this status by its system key, so renaming it is safe.">used by the system: {system}</span></div>}
        </td>
        <td className="num">{s.tickets ? <Link to={`/tickets?status=${s.id}&view=all`}>{s.tickets.toLocaleString()}</Link> : "0"}</td>
        <td className="center"><input type="checkbox" checked={s.isClosed} disabled={busy || !!s.systemKey}
          title={s.systemKey ? "Used by the system: whether it counts as finished can't be changed" : "Counts as finished in reports and searches"}
          aria-label={`${s.name} counts as finished`} onChange={(e) => void put(s, { isClosed: e.target.checked }, `${s.name} ${e.target.checked ? "now counts" : "no longer counts"} as finished.`)} /></td>
        <td className="center"><input type="checkbox" checked={s.inWorkQueue} disabled={busy} aria-label={`${s.name} in the work queue`}
          onChange={(e) => void put(s, { inWorkQueue: e.target.checked }, `${s.name} ${e.target.checked ? "shows" : "no longer shows"} in the work queue.`)} /></td>
        <td className="nowrap">
          {s.isActive && !s.systemKey && moving?.id !== s.id && <button type="button" className="tb-btn" disabled={busy} onClick={() => void retire(s)}>
            {s.tickets ? `Move ${s.tickets.toLocaleString()} ticket${s.tickets === 1 ? "" : "s"} & retire…` : "Retire"}</button>}
          {moving?.id === s.id && (
            <span className="inline-form">
              <select value={moving.to} aria-label="Move the tickets to" onChange={(e) => setMoving({ id: s.id, to: e.target.value })}>
                <option value="">Move to…</option>
                {active.filter((x) => x.id !== s.id).sort((a, b) => a.name.localeCompare(b.name)).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <button type="button" className="tb-btn primary" disabled={busy || !moving.to} onClick={() => void moveAndRetire(s, Number(moving.to))}>Move & retire</button>
              <button type="button" className="icon-link" aria-label="Cancel" onClick={() => setMoving(null)}><X size={15} /></button>
            </span>
          )}
          {!s.isActive && <button type="button" className="tb-btn" disabled={busy} onClick={() => void put(s, { isActive: true }, `${s.name} is active again (at its old place in the list).`)}>Restore</button>}
        </td>
      </tr>
    );
  };

  return (
    <div className="admin-page">
      <div className="admin-head">
        <h2>Statuses</h2>
        <ListsSwitch on="statuses" />
        <form className="inline-form" onSubmit={(e) => { e.preventDefault(); void add(); }}>
          <input value={newName} placeholder="New status name" maxLength={60} aria-label="New status name" onChange={(e) => setNewName(e.target.value)} />
          <button type="submit" className="tb-btn primary" disabled={busy || !newName.trim()}><Plus size={15} />Add status</button>
        </form>
      </div>
      <p className="hint">The order here is the workflow order. Renaming is always safe: the app finds the statuses it uses by their system key, not their name.
        A status can be retired once no ticket is at it; ones the system uses can't be retired.</p>
      {saved && <p className="ok">{saved}</p>}
      {problem && <p className="error">{problem}</p>}
      {error && <p className="error">{error.message}</p>}
      <table className="t-plain admin statuses">
        <thead><tr><th>Order</th><th>Status</th><th className="num">Tickets</th><th className="center">Finished</th><th className="center">In work queue</th><th></th></tr></thead>
        <tbody>
          {active.map(row)}
          {retired.length > 0 && <tr className="sub"><td colSpan={6}><strong>Retired</strong> (kept for old tickets' history, not offered)</td></tr>}
          {retired.map((s) => row(s, -1))}
          {!data && !error && <tr><td colSpan={6} className="hint">Loading…</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
